import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2, Building2, Flame, ShieldCheck, ChefHat, Zap } from "lucide-react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";

const FIRE_ELEMENT_LABELS = {
  extintor: "Extintor", bie: "BIE", detector_fum: "Detector fum",
  detector_gas: "Detector gas", central_alarma: "Central alarma",
  portes_tallafoc: "Portes tallafoc", senyalitzacio: "Senyalització",
  llums_emergencia: "Llums emergència", rociadors: "Rociadors", altres: "Altres",
};

export default function TechnicalDossier({ fire, gas, equipment, electrical, maintenances }) {
  const { currentVenue } = useVenue();
  const [generating, setGenerating] = useState(false);
  const [dossierUrl, setDossierUrl] = useState(null);

  const today = new Date();

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("ca-ES") : "—";
  const daysUntil = (d) => {
    if (!d) return null;
    return Math.ceil((new Date(d) - today) / 86400000);
  };

  const getStatusIcon = (days) => {
    if (days === null) return "—";
    if (days < 0) return "⚠️ CADUCADA";
    if (days <= 30) return `⏰ En ${days} dies`;
    if (days <= 90) return `🕐 En ${days} dies`;
    return `✅ En ${days} dies`;
  };

  const generateDossier = async () => {
    setGenerating(true);

    const sections = [];

    sections.push(`# DOSSIER TÈCNIC DEL LOCAL\n**${currentVenue?.name || "Local"}**\nData: ${new Date().toLocaleDateString("ca-ES")}\n---\n`);

    sections.push(`## 1. PROTECCIÓ CONTRA INCENDIS\nTotal elements: ${fire.length}\n`);
    if (fire.length > 0) {
      fire.forEach(el => {
        sections.push(`### ${FIRE_ELEMENT_LABELS[el.element_type] || el.element_type} — ${el.name}`);
        sections.push(`- Ubicació: ${el.location || "—"}`);
        sections.push(`- Capacitat: ${el.capacity || "—"}`);
        sections.push(`- Marca / Model: ${[el.brand, el.model].filter(Boolean).join(" / ") || "—"}`);
        sections.push(`- Empresa mantenidora: ${el.maintenance_company || "—"}`);
        sections.push(`- Última revisió: ${formatDate(el.last_revision_date)}`);
        sections.push(`- Propera revisió: ${formatDate(el.next_revision_date)} ${getStatusIcon(daysUntil(el.next_revision_date))}`);
        sections.push(`- Estat: ${el.status}\n`);
      });
    } else {
      sections.push("_Cap element de protecció registrat._\n");
    }

    sections.push(`## 2. INSTAL·LACIÓ DE GAS\nTotal instal·lacions: ${gas.length}\n`);
    if (gas.length > 0) {
      gas.forEach(inst => {
        sections.push(`### ${inst.name}`);
        sections.push(`- Tipus: ${inst.installation_type?.replace(/_/g, " ")}`);
        sections.push(`- Empresa instal·ladora: ${inst.installer_company || "—"} (Llicència: ${inst.installer_license || "—"})`);
        sections.push(`- Empresa mantenidora: ${inst.maintenance_company || "—"}`);
        sections.push(`- Última inspecció: ${formatDate(inst.last_inspection_date)}`);
        sections.push(`- Propera inspecció: ${formatDate(inst.next_inspection_date)} ${getStatusIcon(daysUntil(inst.next_inspection_date))}`);
        const openInc = (inst.incidents || []).filter(i => !i.resolved);
        if (openInc.length > 0) sections.push(`- ⚠️ Incidències obertes: ${openInc.length}`);
        sections.push(`- Estat: ${inst.status}\n`);
      });
    } else {
      sections.push("_Cap instal·lació de gas registrada._\n");
    }

    sections.push(`## 3. EQUIPS DE CUINA\nTotal equips: ${equipment.length}\n`);
    if (equipment.length > 0) {
      equipment.forEach(eq => {
        sections.push(`### ${eq.name} (${eq.category})`);
        sections.push(`- Marca / Model: ${[eq.brand, eq.model].filter(Boolean).join(" / ") || "—"}`);
        sections.push(`- Núm. sèrie: ${eq.serial_number || "—"}`);
        sections.push(`- Ubicació: ${eq.location || "—"}`);
        sections.push(`- Empresa manteniment: ${eq.maintenance_company || "—"}`);
        sections.push(`- Últim manteniment: ${formatDate(eq.last_maintenance_date)}`);
        sections.push(`- Proper manteniment: ${formatDate(eq.next_maintenance_date)} ${getStatusIcon(daysUntil(eq.next_maintenance_date))}`);
        sections.push(`- Estat: ${eq.status}\n`);

        const eqMaints = maintenances.filter(m => m.asset_id === eq.id);
        if (eqMaints.length > 0) {
          sections.push(`  **Historial manteniment:**`);
          eqMaints.slice(0, 5).forEach(m => {
            sections.push(`  - ${formatDate(m.maintenance_date)}: ${m.maintenance_type?.replace(/_/g, " ")} — ${m.result}`);
          });
          sections.push("");
        }
      });
    } else {
      sections.push("_Cap equip registrat._\n");
    }

    sections.push(`## 4. INSTAL·LACIÓ ELÈCTRICA\nTotal instal·lacions: ${electrical.length}\n`);
    if (electrical.length > 0) {
      electrical.forEach(inst => {
        sections.push(`### ${inst.name}`);
        sections.push(`- Tipus: ${inst.installation_type?.replace(/_/g, " ")}`);
        sections.push(`- ITC-BT: ${inst.rebt_itc || "—"}`);
        sections.push(`- Última inspecció: ${formatDate(inst.last_inspection_date)}`);
        sections.push(`- Propera inspecció: ${formatDate(inst.next_inspection_date)} ${getStatusIcon(daysUntil(inst.next_inspection_date))}`);
        sections.push(`- Estat: ${inst.status}\n`);
      });
    } else {
      sections.push("_Cap instal·lació elèctrica registrada._\n");
    }

    const content = sections.join("\n");

    // Generate via LLM for a nice formatted summary
    const summary = await hosys.integrations.Core.InvokeLLM({
      prompt: `Ets un expert en seguretat tècnica de locals de restauració. 
Tens el següent dossier tècnic d'un local en format markdown:

${content}

Genera un resum executiu professional en català (màxim 200 paraules) que destaqui:
1. El nivell global de compliment
2. Els punts crítics que requereixen acció immediata
3. Les accions recomanades prioritàries

Respon NOMÉS amb el resum, sense cap títol addicional.`,
    });

    const fullContent = `${content}\n---\n## RESUM EXECUTIU\n${summary}\n\n*Dossier generat automàticament el ${new Date().toLocaleString("ca-ES")}*`;

    const blob = new Blob([fullContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    setDossierUrl(url);
    setGenerating(false);
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = dossierUrl;
    a.download = `dossier-tecnic-${currentVenue?.name?.toLowerCase().replace(/\s+/g, "-") || "local"}-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#7B2D3F]" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Dossier Tècnic del Local</h3>
            <p className="text-xs text-gray-500">Recopilació completa de tots els registres tècnics</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { icon: Flame, label: "Incendis", count: fire.length, color: "bg-red-100 text-red-600" },
            { icon: ShieldCheck, label: "Gas", count: gas.length, color: "bg-orange-100 text-orange-600" },
            { icon: ChefHat, label: "Equips", count: equipment.length, color: "bg-blue-100 text-blue-600" },
            { icon: Zap, label: "Elèctrica", count: electrical.length, color: "bg-yellow-100 text-yellow-600" },
          ].map(s => (
            <div key={s.label} className={`rounded-xl p-3 ${s.color.split(" ")[0]} flex items-center gap-2`}>
              <s.icon className={`w-5 h-5 ${s.color.split(" ")[1]}`} />
              <div>
                <p className={`text-lg font-bold ${s.color.split(" ")[1]}`}>{s.count}</p>
                <p className="text-xs text-gray-600">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm text-gray-600 mb-4">
          El dossier inclourà tota la informació registrada de protecció contra incendis, 
          instal·lació de gas, equips de cuina i instal·lació elèctrica, 
          amb un resum executiu generat per IA.
        </p>

        <div className="flex gap-3">
          <Button
            onClick={generateDossier}
            disabled={generating}
            className="flex-1 bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-2"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generant dossier...</>
            ) : (
              <><FileText className="w-4 h-4" /> Generar Dossier Tècnic</>
            )}
          </Button>
          {dossierUrl && (
            <Button onClick={download} variant="outline" className="gap-2">
              <Download className="w-4 h-4" /> Descarregar
            </Button>
          )}
        </div>
      </div>

      {dossierUrl && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-green-800">Dossier generat correctament</p>
            <p className="text-xs text-green-600">Fes clic a "Descarregar" per obtenir el document.</p>
          </div>
        </div>
      )}
    </div>
  );
}